/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 19th 2022, 11:35:26 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { isEmpty } from "lodash";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { FileText } from "react-feather";
import { FormConfiguration, FORM_TYPES } from "lib/schemas";
import { useFormsAPI } from "form-builder/services";
import { COLORSCHEME } from "theme/styles";
import {
  OSActionButton,
  APIListProgressWrapper,
  StyledOSDialog,
  useToggleState,
  ActionDialog,
  EmphasisText,
} from "ui-widgets";
import { SearchInputWidget } from "ui-widgets/search/SearchInputWidget";

export interface AddExistingFormButtonProps
  extends Pick<FormConfiguration, "form_type">,
    Pick<React.ComponentProps<typeof OSActionButton>, "size"> {
  buttonTitle?: React.ReactNode;
  onChooseForm: (form: FormConfiguration) => void;
  allowedFormTypes?: FormConfiguration["form_type"][];
}

export const AddExistingFormButton = (props: AddExistingFormButtonProps) => {
  const {
    buttonTitle = "Choose Existing",
    form_type: initialFormType,
    onChooseForm,
    size,
    allowedFormTypes = [],
  } = props;
  const { useAPIListForms } = useFormsAPI();
  const [searchText, setSearchText] = React.useState<string>(null);

  const {
    fetchList,
    items: forms,
    isAPIError,
    isMakingAPIRequest,
    httpResponseStatusCode,
  } = useAPIListForms({ makeRequestOnInitialMount: false, apiQueryParams: { form_type: initialFormType } });

  const [toggledFormType, setToggledFormType] = React.useState<FormConfiguration["form_type"]>(initialFormType);

  const onToggleFormType = React.useCallback(
    (changedFormType: FormConfiguration["form_type"]) => {
      fetchList({ apiQueryParams: { form_type: changedFormType } });
      setToggledFormType(changedFormType);
    },
    [fetchList]
  );
  const showFormTypeToggle = allowedFormTypes && allowedFormTypes?.length > 0;

  const filteredForms = React.useMemo(() => {
    if (isEmpty(searchText)) {
      return forms;
    }
    return forms.filter((form) => form.title.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(forms), searchText]);

  const { handleClose, handleOpen, open } = useToggleState();

  const handleOpenDialog = React.useCallback(() => {
    fetchList();
    handleOpen();
  }, [fetchList, handleOpen]);

  const handleChooseForm = (form: FormConfiguration) => {
    onChooseForm(form);
    handleClose();
  };

  return (
    <>
      <OSActionButton
        actionStyle="custom"
        customIcon={<PlaylistAddIcon />}
        variant="contained"
        size={size}
        onClick={handleOpenDialog}
      >
        {buttonTitle}
      </OSActionButton>
      <StyledOSDialog
        open={open}
        onClose={handleClose}
        popUpIcon={<PlaylistAddIcon color="primary" />}
        popUpTitle="Choose an Existing Form"
        popUpDescription="Choose an existing form from a list of forms you have previously created."
      >
        <APIListProgressWrapper
          isAPIError={isAPIError}
          items={forms}
          httpResponseStatusCode={httpResponseStatusCode}
          showLoader={isMakingAPIRequest}
        >
          <Stack sx={{ minHeight: "50vh" }}>
            {showFormTypeToggle ? (
              <div>
                <FormTypeToggler
                  allowedFormTypes={allowedFormTypes}
                  toggledFormType={toggledFormType as any}
                  onToggleFormType={onToggleFormType}
                />
              </div>
            ) : null}
            <div style={{ marginLeft: "auto" }}>
              <SearchInputWidget extraSearchProps={{ handleSearch: (searchText) => setSearchText(searchText) }} />
            </div>
            <List component="nav" aria-label="forms-list">
              {filteredForms.map((form) => (
                <ExistingFormListItem key={form.id} form={form} onChooseForm={handleChooseForm} />
              ))}
            </List>
          </Stack>
        </APIListProgressWrapper>
      </StyledOSDialog>
    </>
  );
};

interface ExistingFormListItemProps {
  form: FormConfiguration;
  onChooseForm: (form: FormConfiguration) => void;
}

const ExistingFormListItem = ({ form, onChooseForm }: ExistingFormListItemProps) => {
  const {
    handleClose: handleCloseConfirmation,
    handleOpen: handleOpenConfirmation,
    open: confirmationOpened,
  } = useToggleState();

  return (
    <>
      <ListItemButton onClick={handleOpenConfirmation}>
        <Grid container>
          <Grid item md={1}>
            <FileText color={COLORSCHEME.primary} />
          </Grid>
          <Grid item md={11}>
            <ListItemText primary={form.title} />
          </Grid>
        </Grid>
      </ListItemButton>
      <ActionDialog
        title="Confirm Choice"
        severity="info"
        open={confirmationOpened}
        onClose={handleCloseConfirmation}
        confirmationAction={() => onChooseForm(form)}
        dialogMessage={
          <>
            Are you sure you want to choose <EmphasisText>{form.title}</EmphasisText>?
          </>
        }
      />
    </>
  );
};

interface FormTypeTogglerProps {
  onToggleFormType: (formType: FormConfiguration["form_type"]) => void;
  toggledFormType: Extract<FormConfiguration["form_type"], "application_form" | "registration_form">;
  allowedFormTypes: FormConfiguration["form_type"][];
}

const FormTypeToggler = ({ allowedFormTypes, toggledFormType, onToggleFormType }: FormTypeTogglerProps) => {
  const toggleFormTypeOptions = allowedFormTypes.length > 0 ? allowedFormTypes : [];

  return (
    <>
      <RadioGroup
        row
        name="form-type-toggle-radio-buttons-group"
        value={`${toggledFormType}`}
        onChange={(event) => onToggleFormType(event.target.value as FormTypeTogglerProps["toggledFormType"])}
      >
        {toggleFormTypeOptions.map((option, index) => (
          <FormControlLabel key={`${index}`} value={option} control={<Radio />} label={FORM_TYPES[option]} />
        ))}
      </RadioGroup>
    </>
  );
};
